/**
 * The potion bonus stage (spec 02 §7.5), ported from level-start.s D_2F7D..D_3080 and
 * platform-collision.s D_3193..routine_3234. D_A428 clears the software sprites, the monsters (D_1E2E)
 * and the item slots; 2x2 items fill empty 2x2 cells band by band from the top (D_2FAC); for 30 s the
 * players collect them, 500 each; the results follow, with PERFECT if every item was taken; then the
 * game goes on to an ordinary level clear (the monster count is 0).
 */

import { addScore } from '../score';
import { ROWS, solidTile } from '../playfield';
import { announce, clearSoftware } from './act';
import { bonusBands } from './choice';
import { emptySlot, timersRun } from './slots';
import {
  BONUS_DRAW_FRAMES, BONUS_MAX, BONUS_PICTURES, BONUS_RESULT_FRAMES, BONUS_SECONDS, BONUS_UNITS, PERFECT_OTHER_UNITS,
  PERFECT_TOP_UNITS, PICKUP_RANGE, SECOND_FRAMES, SLOT_X, SLOT_Y,
} from './tuning';
import type { BonusStage, ItemSystem } from './state';
import type { World } from '../types';

const I_TICK_FRAMES = 2;

/**
 * D_2FAC..$304B: 2-row bands from map row 1, each scanned right to left in column pairs (28,29) ..
 * (2,3); a pair empty on both rows takes an item. After `bands` bands - or once 85 are placed - it
 * stops; with none placed it scans one more band. (With 0 bands, level 43, the C64 reads on past the
 * map into other RAM; here it stops at the bottom row.)
 */
export function bonusCells(world: World, bands: number): { col: number; row: number }[] {
  const out: { col: number; row: number }[] = [];
  const empty = (c: number, r: number): boolean => !solidTile(world.playfield, c, r);
  let left = bands;
  for (let band = 0; ; band++) {
    const row = 1 + 2 * band;
    if (row + 1 > ROWS - 1) break;
    for (let y0 = 26; y0 >= 0; y0 -= 2) {
      const col = y0 + 2;
      if (empty(col, row) && empty(col + 1, row) && empty(col, row + 1) && empty(col + 1, row + 1)) out.push({ col, row });
    }
    if (out.length >= BONUS_MAX) break;
    if (--left === 0) {
      if (out.length > 0) break;
      left = 1;
    }
  }
  return out;
}

/** A potion taken: the stage begins. */
export function takePotion(world: World, sys: ItemSystem, potion: number): void {
  // Its timer runs only if TXTTAB does (not under the clock, not with the Baron out): see the report.
  const timed = timersRun(world);
  clearSoftware(world);
  // D_1E2E: the six monster slots zeroed - the monsters, and the death items that are their sprites.
  sys.stash.push(...world.monsters.splice(0));
  for (let i = world.items.length - 1; i >= 0; i--) if (world.items[i]?.kind === 'death') world.items.splice(i, 1);
  emptySlot(world, sys.food);
  emptySlot(world, sys.powerup);
  // The level timer ($2A) becomes the stage's: the HURRY UP countdown is off meanwhile.
  world.flow.timer = -1;
  const pic = BONUS_PICTURES.find((p) => p.potion === potion) ?? BONUS_PICTURES[0];
  const ids: number[] = [];
  for (const c of bonusCells(world, bonusBands(world.levelIndex))) {
    const id = world.nextId++;
    ids.push(id);
    world.items.push({
      id, kind: 'bonus', item: pic?.food ?? 43, x: c.col * 8 + SLOT_X, y: c.row * 8 + SLOT_Y, value: BONUS_UNITS,
      life: BONUS_SECONDS, falling: false,
    });
  }
  sys.bonus = {
    potion, phase: 'draw', frames: BONUS_DRAW_FRAMES, seconds: BONUS_SECONDS, secondFrames: SECOND_FRAMES, timed,
    total: ids.length, left: ids.length, counts: [0, 0], ids, perfect: false,
  };
  announce(world, 'bonusStage');
}

/** D_31A2's pickup, every frame on the C64 (players move on I-ticks): state 1 only, player 2 first. */
function collect(world: World, sys: ItemSystem, b: BonusStage): void {
  for (const id of [1, 0] as const) {
    const p = world.players[id];
    if (p.state !== 'play' || p.invulnerable > 0 || sys.heartOn[id]) continue;
    for (let i = world.items.length - 1; i >= 0; i--) {
      const it = world.items[i];
      if (!it || it.kind !== 'bonus' || !b.ids.includes(it.id)) continue;
      if (Math.abs(p.x - it.x) >= PICKUP_RANGE || Math.abs(p.y - it.y) >= PICKUP_RANGE) continue;
      world.items.splice(i, 1);
      b.left--;
      b.counts[id]++;
      addScore(world, id, BONUS_UNITS);
      world.events.push({ type: 'collect', player: id, itemId: it.id, value: BONUS_UNITS });
    }
  }
}

/** D_3080: the results; with every item taken, PERFECT for each player in the game (player 2 first). */
function results(world: World, b: BonusStage): void {
  b.phase = 'results';
  b.frames = BONUS_RESULT_FRAMES;
  b.perfect = b.left === 0;
  for (let i = world.items.length - 1; i >= 0; i--) {
    const it = world.items[i];
    if (it && b.ids.includes(it.id)) world.items.splice(i, 1);
  }
  if (!b.perfect) return;
  for (const id of [1, 0] as const) {
    const p = world.players[id];
    if (p.state === 'out' || p.state === 'gameOver') continue;
    const other = b.counts[id === 0 ? 1 : 0];
    addScore(world, id, b.counts[id] >= other ? PERFECT_TOP_UNITS : PERFECT_OTHER_UNITS);
    world.events.push({ type: 'perfect', player: id });
  }
}

/** The stage over: the monsters come back as gone (their slots were zeroed), and the flow clears the level. */
function finish(world: World, sys: ItemSystem): void {
  for (const m of sys.stash) {
    m.state = 'dead';
    m.bubbleId = -1;
  }
  world.monsters.push(...sys.stash);
  sys.stash = [];
  sys.bonus = null;
  sys.bonusDone = true;
}

/** One I-tick of the stage. */
export function bonusTick(world: World, sys: ItemSystem): void {
  const b = sys.bonus;
  if (!b) return;
  world.flow.timer = -1;
  if (b.phase === 'draw') {
    b.frames -= I_TICK_FRAMES;
    if (b.frames <= 0) b.phase = 'run';
    return;
  }
  if (b.phase === 'run') {
    collect(world, sys, b);
    if (b.timed) {
      b.secondFrames -= I_TICK_FRAMES;
      if (b.secondFrames <= 0) {
        b.secondFrames += SECOND_FRAMES;
        b.seconds--;
      }
    }
    // routine_3234 leaves at $2A = 0; D_31A2 once D_597F is 0.
    if (b.left <= 0 || b.seconds <= 0) results(world, b);
    return;
  }
  b.frames -= I_TICK_FRAMES;
  if (b.frames <= 0) finish(world, sys);
}
